import { useActiveVkuiLocation, useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import type { ReactNode } from 'react';
import { api } from '../../api/client';
import { useLoader } from '../../api/loader';
import { usePagedLoader } from '../../api/paging';
import { useSession } from '../../api/session';
import {
  ABILITY_KIND_LABELS,
  type AbilityKind,
  type Author,
  type Page,
  type QueueCounters,
} from '../../api/types';
import { TASK_QUEUE_PARAM } from '../../app/router';
import { t } from '../../i18n';
import { versionLabel } from '../../shared/lib/docNumber';
import { useKeptState } from '../../shared/lib/keptState';
import {
  CardHeading,
  CardLink,
  CardList,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  Gap,
  LoadingState,
  Media,
  Meta,
  Muted,
  NoAccess,
  Screen,
  Section,
  SectionTitle,
  StatusBadge,
  TabRow,
  Thumb,
  type FilterOption,
  type TabRowOption,
  type ThumbKind,
} from '../../shared/ui';
import { DecisionLogSection } from '../decisions/DecisionLog';
import {
  QUEUE_KEEP,
  ShowMore,
  canReviewContent,
  canReviewForms,
  formatDateTime,
  isStaffSession,
  personName,
} from './staffKit';

/**
 * Хаб очередей. Четыре очереди и журнал решений живут одной панелью: у них
 * общая шапка, общий счётчик и одно и то же действие — открыть строку и
 * решить. Разносить их по отдельным экранам значило бы пять раз строить
 * один и тот же список.
 */

/* ── Строки очередей: их же читают экраны решений ────────────────────────── */

export interface FormQueueRow {
  versionId: number;
  nameRu: string | null;
  nameEn: string | null;
  author: Author;
  submittedAt: string | null;
  /** Персонаж ещё ни разу не опубликован: сравнивать не с чем. */
  isInitial: boolean;
  changedFieldsCount: number;
  /** Внешность заявленной версии: кадр в строке очереди. */
  imageId: string | null;
}

/**
 * Общее у строк очередей имущества: предмет, питомец и способность приходят
 * одинаково — версия на проверке плюс снимок того, что опубликовано.
 */
export interface BaseQueueRow {
  versionId: number;
  characterName: string | null;
  author: Author;
  /** Порядковый номер версии, 1..N в пределах сущности: его видит человек. */
  versionNo: number;
  /**
   * Счётчик правок заявки для оптимистичной блокировки: уходит обратно вместе
   * с решением модератора. На экране ему места нет — не путать с versionNo.
   */
  revision: number;
  submittedAt: string | null;
  /** Первая заявка по сущности: сравнивать не с чем. */
  isInitial: boolean;
}

export interface ItemVersionSnapshot {
  /** Номер одобренной версии: с ней сравнивают заявку. */
  versionNo: number;
  title: string;
  description: string | null;
  imageId: string | null;
}

export interface ItemQueueRow extends BaseQueueRow {
  title: string;
  description: string | null;
  imageId: string | null;
  /** Что сейчас лежит на складе публично. Правка её не трогает. */
  published: ItemVersionSnapshot | null;
}

export interface PetVersionSnapshot {
  versionNo: number;
  name: string;
  species: string | null;
  description: string | null;
  imageId: string | null;
  passportImageId: string | null;
}

export interface PetQueueRow extends BaseQueueRow {
  name: string;
  species: string | null;
  description: string | null;
  imageId: string | null;
  passportImageId: string | null;
  published: PetVersionSnapshot | null;
}

export interface AbilityVersionSnapshot {
  versionNo: number;
  kind: AbilityKind;
  title: string;
  description: string | null;
  sourceLink: string | null;
  imageId: string | null;
  expiresAt: string | null;
}

export interface AbilityQueueRow extends BaseQueueRow {
  /** Что заявили: способность или дебаф. Откуда — вопрос к sourceLink. */
  kind: AbilityKind;
  title: string;
  description: string | null;
  /** Ссылка на квест, пост или отыгрыш. Необязательна: игрок мог не приложить. */
  sourceLink: string | null;
  /** Картинка выдачи, если она есть: id строки images, как у версии предмета. */
  imageId: string | null;
  expiresAt: string | null;
  published: AbilityVersionSnapshot | null;
}

type TaskTab = 'form' | 'item' | 'pet' | 'ability' | 'log';

type FormFilter = 'all' | 'initial' | 'edits';

const FORM_FILTERS: readonly FilterOption<FormFilter>[] = [
  { value: 'all', label: t('common.empty.all') },
  { value: 'initial', label: t('review.queue.filter.initial') },
  { value: 'edits', label: t('review.queue.filter.edits') },
];

/**
 * Вкладку из query читаем один раз за жизнь документа — это прямая ссылка вида
 * /tasks?queue=item; дальше она в памяти экрана. Обратно в адрес не пишем:
 * иначе «назад» в ВК начнёт отматывать клики по табам (см. router.ts).
 */
function initialTab(): TaskTab {
  const hash = window.location.hash;
  const start = hash.indexOf('?');
  if (start < 0) return 'form';
  const value = new URLSearchParams(hash.slice(start + 1)).get(TASK_QUEUE_PARAM);
  return value === 'item' || value === 'pet' || value === 'ability' || value === 'log'
    ? value
    : 'form';
}

export function TaskQueuePanel({ id }: NavIdProps) {
  const session = useSession();
  const { panel } = useActiveVkuiLocation();
  const active = id !== undefined && panel === id;

  const seesForms = canReviewForms(session);
  const seesContent = canReviewContent(session);
  const [tab, setTab] = useKeptState<TaskTab>('tasks.tab', initialTab);

  const sizes = useLoader<QueueCounters>(
    (signal) => api.get<QueueCounters>('/api/review/queues', undefined, signal),
    [],
    active && isStaffSession(session),
  );

  if (!isStaffSession(session)) {
    return (
      <Screen id={id} title={t('review.hub.title')}>
        <NoAccess>{t('review.hub.noAccess')}</NoAccess>
      </Screen>
    );
  }

  // null у счётчика — не ноль, а «эта очередь вашей роли не видна»: TabRow
  // такой значок не рисует вовсе, а ноль показывает честно.
  const tabs: TabRowOption<TaskTab>[] = [];
  if (seesForms) {
    tabs.push({
      value: 'form',
      label: t('review.queue.name.form'),
      count: sizes.data?.forms ?? null,
    });
  }
  if (seesContent) {
    tabs.push({
      value: 'item',
      label: t('review.queue.name.item'),
      count: sizes.data?.items ?? null,
    });
    tabs.push({ value: 'pet', label: t('common.section.pets'), count: sizes.data?.pets ?? null });
    tabs.push({
      value: 'ability',
      label: t('common.section.abilities'),
      count: sizes.data?.abilities ?? null,
    });
  }
  tabs.push({ value: 'log', label: t('review.queue.name.log') });

  // Вкладка из ссылки может быть недоступна этой роли — тогда берём первую свою.
  const current = tabs.some((option) => option.value === tab) ? tab : tabs[0].value;

  const counts = tabs
    .map((option) => option.count)
    .filter((count): count is number => typeof count === 'number');
  const pending = counts.length === 0 ? null : counts.reduce((sum, count) => sum + count, 0);

  /*
    В шапке — сколько всего заявок ждёт ЭТУ роль: сумма тех очередей,
    которые ей вообще видны. Разбивку по видам показывают вкладки.
  */
  const tabRow = <TabRow options={tabs} value={current} onChange={setTab} />;

  return (
    <Screen id={id} title={t('review.hub.title')} count={pending} loud>
      {/* Очередь — раздел экрана, а не срез одного списка. Ряд разделов и
          фильтры очереди — одна секция навигации, поэтому очередь с фильтрами
          ставит ряд первым в свою полосу сама; у остальных он стоит один. */}
      {current === 'form' ? <FormsQueue tabs={tabRow} active={active} /> : null}
      {current === 'item' ? (
        <>
          <Section>{tabRow}</Section>
          <ItemsQueue active={active} />
        </>
      ) : null}
      {current === 'pet' ? (
        <>
          <Section>{tabRow}</Section>
          <PetsQueue active={active} />
        </>
      ) : null}
      {current === 'ability' ? (
        <>
          <Section>{tabRow}</Section>
          <AbilitiesQueue active={active} />
        </>
      ) : null}
      {current === 'log' ? <DecisionLogSection tabs={tabRow} active={active} /> : null}
    </Screen>
  );
}

/* ── Строка очереди ──────────────────────────────────────────────────────── */

interface QueueRowProps {
  /** Что проверяем: название вещи или имя персонажа из анкеты. */
  title: string;
  /** Картинка проверяемой версии. У анкеты её в очереди нет — будет заглушка. */
  imageId: string | null;
  /** Чья это вещь: заглушка кадра носит её значок. У анкеты вещи нет. */
  kind?: ThumbKind;
  /** Чей персонаж. У анкеты он же и заголовок, второй раз его не пишут. */
  characterName: string | null;
  author: Author;
  submittedAt: string | null;
  /** Сравнивать не с чем: по этому признаку проверяющий выбирает, что открыть. */
  isInitial: boolean;
  /** Чем эта заявка отличается от соседней: номер версии, вид, счёт полей. */
  meta: ReactNode[];
  onOpen: () => void;
}

/**
 * Одна строка на все четыре очереди.
 *
 * Общая не ради экономии: проверяющий идёт по вкладкам подряд, и строки,
 * собранные по-разному, он читает заново на каждой. До этого у предметов не
 * было картинки, у остальных была, а все факты были склеены в один subtitle
 * SimpleCell — тот не переносит строки, и на 375 px хвост съедало многоточие.
 *
 * Нажимается строка целиком: кнопка «Разобрать» под каждой карточкой забирала
 * полосу высотой с саму строку, а цель у карточки всё равно одна.
 */
function QueueRow({
  title,
  imageId,
  kind,
  characterName,
  author,
  submittedAt,
  isInitial,
  meta,
  onOpen,
}: QueueRowProps) {
  return (
    <CardLink tone="pending" label={t('review.queue.open', { title })} chevron onClick={onOpen}>
      {/* Кадр в строке стоит всегда, у анкет он всегда с заглушкой: список
          отдаёт минимум и картинки в нём нет. Так строки очереди выстраиваются
          в колонку, а не расходятся на две формы. */}
      <Media
        aside={<Thumb image={imageId === null ? null : { id: imageId }} kind={kind} size="row" />}
      >
        {/* Значок только у первой заявки: правку различает номер версии в
            мете, и второй значок рядом с ней ничего не добавлял. */}
        <CardHeading
          badge={
            isInitial ? (
              <StatusBadge tone="published">{t('review.queue.initial')}</StatusBadge>
            ) : undefined
          }
        >
          {title}
        </CardHeading>

        {characterName === null ? null : (
          <>
            <Gap step="s1" />
            <Muted>{characterName}</Muted>
          </>
        )}

        <Gap step="s1" />
        <Meta>{[formatDateTime(submittedAt), ...meta, personName(author)]}</Meta>
      </Media>
    </CardLink>
  );
}

/* ── Очередь анкет ───────────────────────────────────────────────────────── */

function FormsQueue({ tabs, active }: { tabs: ReactNode; active: boolean }) {
  const routeNavigator = useRouteNavigator();
  const [filter, setFilter] = useKeptState<FormFilter>('tasks.formFilter', 'all');

  const queue = usePagedLoader<FormQueueRow>(
    (params, signal) =>
      api.get<Page<FormQueueRow>>(
        '/api/characters/queue',
        { filter, limit: params.limit, offset: params.offset },
        signal,
      ),
    [filter],
    active,
    QUEUE_KEEP.form,
  );

  // Как в реестре: заявки прошлого фильтра под новым читались бы как его и открывались бы.
  const page = queue.leftover ? null : queue.data;

  return (
    <>
      {/* Фильтр — в секции навигации под очередями, а подпись со счётом — над
          самим списком: она называет то, что под ней. */}
      <Section>
        {tabs}
        <FilterBar>
          <FilterSelect
            label={t('review.queue.filter.kind')}
            options={FORM_FILTERS}
            value={filter}
            onChange={setFilter}
          />
        </FilterBar>
      </Section>

      <Section>
        <SectionTitle count={page?.total}>{t('review.queue.name.form')}</SectionTitle>

        {queue.loading && !page ? <LoadingState text={t('review.queue.loading')} /> : null}
        {queue.error ? <ErrorState message={queue.error} onRetry={queue.reload} /> : null}

        {page && page.items.length === 0 ? (
          <EmptyState title={t('review.queue.empty.title')}>
            {t('review.queue.empty.form')}
          </EmptyState>
        ) : null}

        <CardList>
          {page?.items.map((row) => (
            <QueueRow
              key={row.versionId}
              title={row.nameRu?.trim() || row.nameEn?.trim() || t('common.empty.noName')}
              imageId={row.imageId}
              characterName={null}
              author={row.author}
              submittedAt={row.submittedAt}
              isInitial={row.isInitial}
              meta={[
                row.isInitial ? null : t('review.queue.changed', { count: row.changedFieldsCount }),
              ]}
              onOpen={() => routeNavigator.push(`/tasks/form/${row.versionId}`)}
            />
          ))}
        </CardList>

        <ShowMore page={page} onMore={queue.loadMore} loading={queue.loading} />
      </Section>
    </>
  );
}

/* ── Очередь предметов ───────────────────────────────────────────────────── */

function ItemsQueue({ active }: { active: boolean }) {
  const routeNavigator = useRouteNavigator();

  const queue = usePagedLoader<ItemQueueRow>(
    (params, signal) =>
      api.get<Page<ItemQueueRow>>(
        '/api/inventory/queue',
        { limit: params.limit, offset: params.offset },
        signal,
      ),
    [],
    active,
    QUEUE_KEEP.item,
  );

  return (
    <Section>
      <SectionTitle count={queue.data?.total}>{t('review.queue.name.item')}</SectionTitle>

      {queue.loading && !queue.data ? <LoadingState text={t('review.queue.loading')} /> : null}
      {queue.error ? <ErrorState message={queue.error} onRetry={queue.reload} /> : null}

      {queue.data && queue.data.items.length === 0 ? (
        <EmptyState title={t('review.queue.empty.title')}>
          {t('review.queue.empty.item')}
        </EmptyState>
      ) : null}

      <CardList>
        {queue.data?.items.map((row) => (
          <QueueRow
            key={row.versionId}
            title={row.title}
            imageId={row.imageId}
            kind="item"
            characterName={row.characterName}
            author={row.author}
            submittedAt={row.submittedAt}
            isInitial={row.isInitial}
            meta={[versionFact(row.versionNo)]}
            onOpen={() => routeNavigator.push(`/tasks/item/${row.versionId}`)}
          />
        ))}
      </CardList>

      <ShowMore page={queue.data} onMore={queue.loadMore} loading={queue.loading} />
    </Section>
  );
}

/* ── Очередь питомцев ────────────────────────────────────────────────────── */

function PetsQueue({ active }: { active: boolean }) {
  const routeNavigator = useRouteNavigator();

  const queue = usePagedLoader<PetQueueRow>(
    (params, signal) =>
      api.get<Page<PetQueueRow>>(
        '/api/pets/queue',
        { limit: params.limit, offset: params.offset },
        signal,
      ),
    [],
    active,
    QUEUE_KEEP.pet,
  );

  return (
    <Section>
      <SectionTitle count={queue.data?.total}>{t('common.section.pets')}</SectionTitle>

      {queue.loading && !queue.data ? <LoadingState text={t('review.queue.loading')} /> : null}
      {queue.error ? <ErrorState message={queue.error} onRetry={queue.reload} /> : null}

      {queue.data && queue.data.items.length === 0 ? (
        <EmptyState title={t('review.queue.empty.title')}>{t('review.queue.empty.pet')}</EmptyState>
      ) : null}

      <CardList>
        {queue.data?.items.map((row) => (
          <QueueRow
            key={row.versionId}
            title={row.name}
            imageId={row.imageId}
            kind="pet"
            characterName={row.characterName}
            author={row.author}
            submittedAt={row.submittedAt}
            isInitial={row.isInitial}
            meta={[versionFact(row.versionNo)]}
            onOpen={() => routeNavigator.push(`/tasks/pet/${row.versionId}`)}
          />
        ))}
      </CardList>

      <ShowMore page={queue.data} onMore={queue.loadMore} loading={queue.loading} />
    </Section>
  );
}

/* ── Очередь способностей ────────────────────────────────────────────────── */

function AbilitiesQueue({ active }: { active: boolean }) {
  const routeNavigator = useRouteNavigator();

  const queue = usePagedLoader<AbilityQueueRow>(
    (params, signal) =>
      api.get<Page<AbilityQueueRow>>(
        '/api/abilities/queue',
        { limit: params.limit, offset: params.offset },
        signal,
      ),
    [],
    active,
    QUEUE_KEEP.ability,
  );

  return (
    <Section>
      <SectionTitle count={queue.data?.total}>{t('review.queue.name.ability')}</SectionTitle>

      {queue.loading && !queue.data ? <LoadingState text={t('review.queue.loading')} /> : null}
      {queue.error ? <ErrorState message={queue.error} onRetry={queue.reload} /> : null}

      {queue.data && queue.data.items.length === 0 ? (
        <EmptyState title={t('review.queue.empty.title')}>
          {t('review.queue.empty.ability')}
        </EmptyState>
      ) : null}

      <CardList>
        {queue.data?.items.map((row) => (
          <QueueRow
            key={row.versionId}
            title={row.title}
            imageId={row.imageId}
            kind="ability"
            characterName={row.characterName}
            author={row.author}
            submittedAt={row.submittedAt}
            isInitial={row.isInitial}
            // Ссылку на источник строка не показывает: она бывает длиной в
            // строку сама по себе, а открывают её всё равно в карточке заявки.
            meta={[ABILITY_KIND_LABELS[row.kind], versionFact(row.versionNo)]}
            onOpen={() => routeNavigator.push(`/tasks/ability/${row.versionId}`)}
          />
        ))}
      </CardList>

      <ShowMore page={queue.data} onMore={queue.loadMore} loading={queue.loading} />
    </Section>
  );
}

/**
 * Номер показываем со второй версии: повторная подача по ещё не опубликованной
 * вещи подписана тем же значком «первая заявка», и отличает её от первой только
 * он. У первой номер — шум.
 */
function versionFact(versionNo: number): string | null {
  return versionNo > 1 ? versionLabel(versionNo) : null;
}
